import React, { useEffect, useMemo, useRef } from 'react';
import { animated, useSpring } from 'react-spring';
import {
    Backdrop,
    Box,
    IconButton,
    Modal,
    Toolbar,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import ClearIcon from '@mui/icons-material/Clear';
import { DAvatarIcon } from '../icon';

interface FadeProps {
    children?: React.ReactElement;
    in: boolean;
    onEnter?: () => void;
    onEntered?: () => void;
    onExited?: () => void;
}

const Fade = React.forwardRef<HTMLDivElement, FadeProps>(function Fade(
    props,
    ref,
) {
    const {
        in: open,
        children,
        onEnter,
        onEntered,
        onExited,
        ...other
    } = props;
    const style = useSpring({
        from: { opacity: 0 },
        to: { opacity: open ? 1 : 0 },
        config: {
            duration: 200,
        },
        onStart: () => {
            if (open && onEnter) {
                onEnter();
            }
        },
        onRest: () => {
            if (open && onEntered) {
                onEntered();
            } else if (!open && onExited) {
                onExited();
            }
        },
    });

    return (
        <animated.div ref={ref} style={style} {...other}>
            {children}
        </animated.div>
    );
});

interface ICustomModalProps {
    children: React.ReactNode | React.ReactNode[];
    open: boolean;
    setOpen: (open: boolean) => void;
    title: string;
    icon?: React.ReactElement<any, any>;
    width?: string;
    height?: string;
    keepMounted?: boolean;
    onExited?: () => void;
    size?: 'xl' | 'large' | 'medium' | 'small';
    tight?: boolean;
    // When true, clicking the backdrop also closes the modal. Defaults to false
    // so data-entry forms can't be dismissed by a stray click outside (losing
    // unsaved work); pass true for read-only / informational modals.
    dismissible?: boolean;
    // Header bar colour. Defaults to primary, which is every normal modal; pass
    // 'warning' for a modal that interrupts to flag something the user should
    // look at before continuing, so it doesn't read as just another dialog.
    toolbarColor?: 'primary' | 'warning';
}

export function DModal(props: ICustomModalProps) {
    const theme = useTheme();

    const {
        children,
        open,
        setOpen,
        title,
        icon,
        height,
        keepMounted = false,
        onExited,
        width,
        size = 'xl',
        tight,
        dismissible = false,
        toolbarColor = 'primary',
    } = props;

    // Fixed (but viewport-capped) heights. A fixed height keeps the modal the
    // SAME size across tab switches, so the form's submit footer never moves —
    // no "fishing" for the button. The px value is the harmonious target; the vh
    // caps it on short screens. Short tabs leave some empty space (acceptable,
    // bounded); tall tabs scroll inside the body. An explicit `height` prop still
    // wins (e.g. the small confirmation modal).
    const computedHeight = useMemo(() => {
        if (height) {
            return height;
        } else if (size === 'xl') {
            return 'min(880px, 90vh)';
        } else if (size === 'large') {
            return 'min(740px, 86vh)';
        } else if (size === 'medium') {
            return 'min(620px, 82vh)';
        } else if (size === 'small') {
            return 'min(520px, 80vh)';
        } else {
            return 'min(620px, 82vh)';
        }
    }, [size, height]);

    // Width scales with the viewport but is capped in px so the modal never gets
    // absurdly wide on large monitors (a form spanning 90vw on a 27" screen is
    // exactly what we're avoiding). `size` / `tight` tune both the vw and the cap.
    const computedWidth = useMemo(() => {
        if (width) {
            return width;
        } else if (size === 'xl') {
            return tight ? 'min(70vw, 1100px)' : 'min(90vw, 1400px)';
        } else if (size === 'large') {
            return tight ? 'min(50vw, 800px)' : 'min(70vw, 1000px)';
        } else if (size === 'medium') {
            return tight ? 'min(40vw, 600px)' : 'min(60vw, 760px)';
        } else if (size === 'small') {
            return tight ? 'min(40vw, 480px)' : 'min(50vw, 560px)';
        } else {
            return 'min(60vw, 760px)';
        }
    }, [size, width, tight]);

    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

    // Escape now requires TWO presses (within the window below) to close, so a
    // single stray Esc can't discard an open form. The first press "arms" and
    // starts a timer; a second press before it elapses closes; otherwise it
    // disarms silently. `escArmedRef` is a ref (not state) because nothing needs
    // to re-render on arming — there is intentionally no visual hint.
    const ESC_DOUBLE_PRESS_MS = 1200;
    const escArmedRef = useRef(false);
    const escTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const modalContentRef = useRef<HTMLDivElement | null>(null);

    const disarmEsc = () => {
        escArmedRef.current = false;
        if (escTimerRef.current) {
            clearTimeout(escTimerRef.current);
            escTimerRef.current = null;
        }
    };

    // Reset the arming whenever the modal toggles, and clean up the timer on
    // unmount, so a stale first-press never carries into the next time it opens.
    useEffect(() => {
        disarmEsc();
        return disarmEsc;
    }, [open]);

    return (
        <Modal
            // MUI fires onClose for both Escape and backdrop clicks (with a
            // `reason`). A backdrop click only closes when the modal is
            // explicitly dismissible (so forms keep unsaved work); Escape needs
            // two presses (see the double-press note above).
            onClose={(_event, reason) => {
                if (reason === 'backdropClick') {
                    if (dismissible) {
                        setOpen(false);
                    }
                    return;
                }
                if (reason === 'escapeKeyDown') {
                    if (escArmedRef.current) {
                        disarmEsc();
                        setOpen(false);
                        return;
                    }
                    escArmedRef.current = true;
                    escTimerRef.current = setTimeout(() => {
                        escArmedRef.current = false;
                        escTimerRef.current = null;
                    }, ESC_DOUBLE_PRESS_MS);
                    return;
                }
                setOpen(false);
            }}
            open={open}
            keepMounted={keepMounted}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 200,
            }}
        >
            <Fade
                ref={modalContentRef}
                in={open}
                onEntered={() => {
                    // MUI's focus trap may move focus after the transition
                    // begins. Restore the active tab after the transition has
                    // settled so the shortcut works immediately on open.
                    modalContentRef.current
                        ?.querySelector<HTMLElement>(
                            '[data-rh-tabs-wrapper="true"] [role="tab"][aria-selected="true"]',
                        )
                        ?.focus();
                }}
                onExited={onExited}
            >
                <Box
                    role={'dialog'}
                    aria-modal={'true'}
                    aria-label={title}
                    sx={(theme) => {
                        return {
                            position: 'absolute',
                            top: isMobile ? '0' : '50%',
                            bottom: undefined,
                            left: isMobile ? '0' : '50%',
                            right: isMobile ? '0' : undefined,
                            transform: isMobile
                                ? undefined
                                : 'translate(-50%, -50%)',
                            bgcolor: theme.palette.background.paper,
                            borderRadius: '0.5rem',
                            boxShadow: 24,
                            overflow: 'hidden',
                            width: isMobile ? '100%' : computedWidth,
                            // Mobile/tablet (incl. iPad): bound the height to the
                            // DYNAMIC viewport (100dvh) instead of the old
                            // top:0/bottom:0 anchoring. On iOS the fixed Modal
                            // root that those anchors resolve against is the
                            // LAYOUT viewport, which is taller than the visible
                            // area whenever Safari's chrome is showing — so
                            // bottom:0, and the sticky Save footer pinned to it,
                            // ended up below the fold with no way to scroll the
                            // fixed modal to reveal them (tall forms could not be
                            // saved). 100dvh tracks the visible area, so the
                            // scroll container is bounded and the footer stays
                            // reachable. Fall back to '-webkit-fill-available'
                            // only if a target device predates dvh (iOS < 15.4).
                            height: isMobile ? '100dvh' : computedHeight,
                            maxHeight: isMobile ? '100dvh' : '95vh',
                            // Flex column so the body fills the space below the
                            // toolbar and a form's sticky footer stays pinned to
                            // the bottom.
                            display: 'flex',
                            flexDirection: 'column',
                        };
                    }}
                >
                    <Toolbar
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            bgcolor: `${toolbarColor}.main`,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                mr: 6,
                                alignItems: 'center',
                            }}
                        >
                            {icon ? (
                                <Box sx={{ mr: 1 }}>
                                    <DAvatarIcon size={'small'}>
                                        {icon}
                                    </DAvatarIcon>
                                </Box>
                            ) : null}
                            <Typography variant={'h5'} sx={{ flexGrow: 1 }}>
                                {title}
                            </Typography>
                        </Box>

                        <IconButton
                            color="inherit"
                            size={'small'}
                            onClick={() => {
                                setOpen(false);
                            }}
                        >
                            <ClearIcon />
                        </IconButton>
                    </Toolbar>
                    <Box
                        sx={{
                            // Fill the space below the toolbar and scroll
                            // internally once content exceeds the modal's
                            // maxHeight. The modal itself hugs content, so for a
                            // short form this stays short (no internal scroll).
                            flex: 1,
                            minHeight: 0,
                            overflowY: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {children}
                    </Box>
                </Box>
            </Fade>
        </Modal>
    );
}
